import {
  App,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Empty,
  Flex,
  Form,
  Grid,
  Skeleton,
  Tag,
  Typography,
  notification
} from "antd"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { ActionTooltip } from "../../../../../../../components/ActionTooltip.tsx"
import {
  FieldHint,
  HintedLabel
} from "../../../../../../../components/FieldHint.tsx"
import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import InputNumber from "../../../../../../../components/Input/InputNumber.tsx"
import {
  ProjectPickerSelect,
  projectPickerLabel
} from "../../../../../../../components/Shared/ProjectPickerSelect.tsx"
import { notificationOptions } from "../../../../../../../constants/notification.config.ts"
import { empty } from "../../../../../../../constants/placeholders.ts"
import { unavailableProps } from "../../../../../../../utils/unavailable-control.ts"
import { useAppDispatch } from "../../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { dateAutoFill } from "../../../../../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import { handleInvoiceApiError } from "../../../../../../Invoices/helpers/handle-invoice-api-error.ts"
import { computeInvoiceTotals } from "../../../../../../Invoices/helpers/invoice-totals.ts"
import {
  deleteInvoice,
  fetchBillableLines,
  generateInvoice,
  issueInvoice,
  patchGeneratedInvoice,
  sendInvoice
} from "../../../../../../Invoices/redux/invoices.thunks.ts"
import type {
  BillableLine,
  GenerateInvoicePayload,
  InvoiceLineInput,
  InvoiceListItem
} from "../../../../../../Invoices/types/invoices.types.ts"
import { fetchUser } from "../../../../../../Settings/redux/settings.thunks.ts"

const { Text, Title } = Typography

type SaveMode = "draft" | "issue" | "send"

interface IssueInvoiceFormValues {
  project?: string
  date_issued?: string
  due_date?: string
  tax_pct?: number | null
  retainage_pct?: number | null
}

interface IssueInvoiceDrawerProps {
  open: boolean
  onClose: () => void
  onSaved: (invoice?: InvoiceListItem) => void
  projectId?: string
  projectLabel?: string
  preselectedLineId?: string
  invoice?: InvoiceListItem
}

interface ProjectOption {
  value: string
  label: string
}

export const IssueInvoiceDrawer = ({
  open,
  onClose,
  onSaved,
  projectId,
  projectLabel,
  preselectedLineId,
  invoice
}: IssueInvoiceDrawerProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { modal } = App.useApp()
  const screens = Grid.useBreakpoint()
  const { getDateValueProps, createDateRangeRules, normalizeDate } =
    useDateFormat()

  const [form] = Form.useForm<IssueInvoiceFormValues>()
  const billableLines = useAppSelector((state) => state.invoices.billableLines)
  const linesFetching = useAppSelector(
    (state) => state.invoices.billableLinesFetching
  )
  const companyDetails = useAppSelector(
    (state) => state.settings.user?.company_details
  )
  const currentUserId = useAppSelector((state) => state.auth.userInfo?.id)

  const [savingMode, setSavingMode] = useState<SaveMode | null>(null)
  const [selectedProject, setSelectedProject] = useState<string | undefined>(
    projectId
  )
  const [seededOption, setSeededOption] = useState<ProjectOption[]>([])
  const [selectedLines, setSelectedLines] = useState<Record<string, number>>({})
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({})
  const baselineRef = useRef<IssueInvoiceFormValues>({})
  const createdDraftRef = useRef<InvoiceListItem | null>(null)

  const isEditMode = Boolean(invoice)

  const taxPct = Form.useWatch("tax_pct", form)
  const retainagePct = Form.useWatch("retainage_pct", form)
  const dateIssued = Form.useWatch("date_issued", form)
  const dueDate = Form.useWatch("due_date", form)
  const saving = savingMode !== null

  const totals = useMemo(
    () =>
      computeInvoiceTotals(Object.values(selectedLines), taxPct, retainagePct),
    [selectedLines, taxPct, retainagePct]
  )

  const extraSelectedLines = useMemo<BillableLine[]>(() => {
    if (!invoice) return []
    const known = new Set(billableLines.map((line) => line.id))
    return Object.keys(selectedLines)
      .filter((id) => !known.has(id))
      .map((id) => {
        const meta = invoice.lines?.find((line) => line.finance_line === id)
        const amount = String(selectedLines[id])
        return {
          id,
          name: meta?.finance_line_name || empty,
          planned_value: amount,
          previously_billed: "0",
          balance_remaining: amount,
          entry_type: "",
          responsible_type: null
        }
      })
  }, [invoice, billableLines, selectedLines])

  const renderLines = useMemo(
    () => [...billableLines, ...extraSelectedLines],
    [billableLines, extraSelectedLines]
  )

  useEffect(() => {
    if (!open) return

    form.resetFields()
    setLineErrors({})
    createdDraftRef.current = null
    if (companyDetails === undefined && currentUserId) {
      void dispatch(fetchUser(currentUserId))
    }

    if (invoice) {
      setSelectedProject(invoice.project as string)
      const editLabel = projectPickerLabel(invoice.project_info)
      if (invoice.project && editLabel) {
        setSeededOption([{ value: invoice.project, label: editLabel }])
      }
      const initialLines: Record<string, number> = {}
      invoice.lines?.forEach((line) => {
        initialLines[line.finance_line] = Number(line.amount) || 0
      })
      setSelectedLines(initialLines)
      form.setFieldsValue({
        date_issued: invoice.date_issued ?? undefined,
        due_date: invoice.due_date ?? undefined,
        tax_pct: invoice.tax_pct != null ? Number(invoice.tax_pct) : null,
        retainage_pct:
          invoice.retainage_pct != null ? Number(invoice.retainage_pct) : null
      })
    } else {
      setSelectedProject(projectId)
      if (projectId && projectLabel) {
        setSeededOption([{ value: projectId, label: projectLabel }])
      }
      setSelectedLines({})
      form.setFieldsValue({
        tax_pct: companyDetails?.default_tax_pct
          ? Number(companyDetails.default_tax_pct)
          : null,
        retainage_pct: companyDetails?.default_retainage_pct
          ? Number(companyDetails.default_retainage_pct)
          : null
      })
    }
    baselineRef.current = form.getFieldsValue()
  }, [open, projectId, projectLabel, invoice])

  useEffect(() => {
    if (!open || !selectedProject) return
    void dispatch(fetchBillableLines(selectedProject))
      .unwrap()
      .then((result) => {
        const { lines } = result
        if (
          preselectedLineId &&
          lines.some((line) => line.id === preselectedLineId)
        ) {
          const line = lines.find((item) => item.id === preselectedLineId)
          setSelectedLines((prev) => ({
            ...prev,
            [preselectedLineId]: Number(line?.balance_remaining) || 0
          }))
        }
        if (!invoice) {
          const current = form.getFieldsValue()
          const patch: Partial<IssueInvoiceFormValues> = {}
          if (current.tax_pct == null && result.default_tax_pct != null) {
            patch.tax_pct = Number(result.default_tax_pct)
          }
          if (
            current.retainage_pct == null &&
            result.default_retainage_pct != null
          ) {
            patch.retainage_pct = Number(result.default_retainage_pct)
          }
          if (Object.keys(patch).length) form.setFieldsValue(patch)
        }
      })
      .catch(() => undefined)
  }, [open, selectedProject, preselectedLineId, invoice, dispatch, form])

  const toggleLine = (line: BillableLine, checked: boolean) => {
    setSelectedLines((prev) => {
      const next = { ...prev }
      if (checked) {
        next[line.id] = Number(line.balance_remaining) || 0
      } else {
        delete next[line.id]
      }
      return next
    })
    setLineErrors((prev) => {
      const next = { ...prev }
      delete next[line.id]
      return next
    })
  }

  const setLineAmount = (line: BillableLine, value: number | null) => {
    const amount = value ?? 0
    setSelectedLines((prev) => ({ ...prev, [line.id]: amount }))
    setLineErrors((prev) => {
      const next = { ...prev }
      if (!Number.isFinite(amount) || amount <= 0) {
        next[line.id] = t("invoices.drawer.invalidAmount")
      } else if (amount > Number(line.balance_remaining)) {
        next[line.id] = t("invoices.drawer.overBilled", {
          balance: budgetFormat(line.balance_remaining)
        })
      } else {
        delete next[line.id]
      }
      return next
    })
  }

  const buildPayload = (
    values: IssueInvoiceFormValues
  ): GenerateInvoicePayload => {
    const lines: InvoiceLineInput[] = Object.entries(selectedLines).map(
      ([financeLine, amount]) => ({
        finance_line: financeLine,
        amount: String(amount)
      })
    )
    return {
      ...(selectedProject ? { project: selectedProject } : {}),
      ...(values.date_issued
        ? { date_issued: normalizeDate(values.date_issued) }
        : {}),
      ...(values.due_date ? { due_date: normalizeDate(values.due_date) } : {}),
      tax_pct: values.tax_pct != null ? String(values.tax_pct) : null,
      retainage_pct:
        values.retainage_pct != null ? String(values.retainage_pct) : null,
      ...(lines.length ? { lines } : {})
    }
  }

  const handleApiError = (payload: unknown) => {
    handleInvoiceApiError({
      payload,
      modal,
      t: t as (key: string, options?: Record<string, unknown>) => string,
      navigate,
      onOverBilled: (errors) =>
        setLineErrors((prev) => ({ ...prev, ...errors }))
    })
  }

  const handleSave = async (mode: SaveMode) => {
    let values: IssueInvoiceFormValues
    try {
      values = await validateFormWithScroll(form)
    } catch {
      return
    }
    if (
      mode !== "draft" &&
      (!selectedProject ||
        !values.date_issued ||
        !values.due_date ||
        Object.keys(selectedLines).length === 0 ||
        Object.keys(lineErrors).length > 0)
    ) {
      return
    }

    setSavingMode(mode)
    try {
      const payload = buildPayload(values)
      const existingId = invoice?.id ?? createdDraftRef.current?.id

      const draft = existingId
        ? await dispatch(
            patchGeneratedInvoice({ id: existingId, ...payload })
          ).unwrap()
        : await dispatch(generateInvoice(payload)).unwrap()

      if (!invoice) createdDraftRef.current = draft

      let finalInvoice = draft
      if (mode === "issue") {
        finalInvoice = await dispatch(issueInvoice(draft.id)).unwrap()
      } else if (mode === "send") {
        const result = await dispatch(sendInvoice({ id: draft.id })).unwrap()
        finalInvoice = result.invoice
      }

      notification.success({
        ...notificationOptions.success,
        message: t(
          mode === "draft"
            ? "invoices.drawer.draftSaved"
            : mode === "issue"
              ? "invoices.drawer.issued"
              : "invoices.drawer.sent"
        )
      })
      createdDraftRef.current = null
      onSaved(finalInvoice)
    } catch (payload) {
      handleApiError(payload)
    } finally {
      setSavingMode(null)
    }
  }

  const cleanupAndClose = () => {
    const draft = createdDraftRef.current
    if (draft && !isEditMode) {
      void dispatch(deleteInvoice(draft.id))
      createdDraftRef.current = null
    }
    onClose()
  }

  const handleClose = () => {
    if (saving) return
    const changed =
      Object.keys(selectedLines).length > 0 ||
      isFormChanged<IssueInvoiceFormValues>(
        form.getFieldsValue(),
        baselineRef.current
      )
    if (changed) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk: cleanupAndClose
      })
    } else {
      cleanupAndClose()
    }
  }

  const projectMissing = !selectedProject
  const datesMissing = !dateIssued || !dueDate
  const normalizedDateIssued = dateIssued ? normalizeDate(dateIssued) : null
  const normalizedDueDate = dueDate ? normalizeDate(dueDate) : null
  const datesInvalid =
    !datesMissing &&
    (!normalizedDateIssued ||
      !normalizedDueDate ||
      normalizedDueDate < normalizedDateIssued)
  const noLinesSelected = Object.keys(selectedLines).length === 0
  const hasLineErrors = Object.keys(lineErrors).length > 0
  const issueDisabled =
    projectMissing ||
    datesMissing ||
    datesInvalid ||
    noLinesSelected ||
    hasLineErrors
  const issueDisabledHint = projectMissing
    ? t("invoices.drawer.selectProjectHint")
    : datesMissing
      ? t("invoices.drawer.selectDatesHint")
      : datesInvalid
        ? t("invoices.drawer.fixDateErrorsHint")
        : noLinesSelected
          ? t("invoices.drawer.selectLineHint")
          : hasLineErrors
            ? t("invoices.drawer.fixLineErrorsHint")
            : undefined

  return (
    <Drawer
      title={t(
        isEditMode ? "invoices.drawer.editTitle" : "invoices.drawer.title"
      )}
      placement="right"
      width={screens.md ? 560 : "100%"}
      open={open}
      onClose={handleClose}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      footer={
        <Flex justify="flex-end" gap="small" wrap>
          <Button onClick={handleClose} disabled={saving}>
            {t("invoices.drawer.cancel")}
          </Button>
          <Button
            loading={savingMode === "draft"}
            disabled={saving && savingMode !== "draft"}
            onClick={() => handleSave("draft")}
          >
            {t("invoices.drawer.saveDraft")}
          </Button>
          <ActionTooltip title={issueDisabledHint}>
            <Button
              loading={savingMode === "issue"}
              // Mid-save is a different state and stays truly disabled.
              disabled={saving && savingMode !== "issue"}
              {...unavailableProps(issueDisabled, () => handleSave("issue"))}
            >
              {t("invoices.drawer.issue")}
            </Button>
          </ActionTooltip>
          <ActionTooltip title={issueDisabledHint}>
            <Button
              type="primary"
              loading={savingMode === "send"}
              disabled={saving && savingMode !== "send"}
              {...unavailableProps(issueDisabled, () => handleSave("send"))}
            >
              {t("invoices.drawer.saveAndSend")}
            </Button>
          </ActionTooltip>
        </Flex>
      }
    >
      <Form<IssueInvoiceFormValues>
        form={form}
        layout="vertical"
        className="with-default-error"
        disabled={saving}
      >
        <Title level={5}>{t("invoices.drawer.sections.details")}</Title>

        <Form.Item
          label={t("invoices.drawer.fields.project")}
          required={false}
          help={
            projectMissing ? t("invoices.drawer.blankDraftHint") : undefined
          }
        >
          <ProjectPickerSelect
            disabled={Boolean(projectId) || isEditMode || saving}
            value={selectedProject}
            initialOptions={seededOption}
            onChange={(value) => {
              setSelectedProject((value as string) ?? undefined)
              setSelectedLines({})
              setLineErrors({})
            }}
          />
        </Form.Item>

        <Flex gap="middle">
          <Form.Item<IssueInvoiceFormValues>
            className="flex-1"
            name="date_issued"
            label={t("invoices.drawer.fields.dateIssued")}
            getValueProps={getDateValueProps}
            dependencies={["due_date"]}
            rules={createDateRangeRules<IssueInvoiceFormValues>(
              "due_date",
              "start"
            )}
          >
            <DatePicker
              className="w-full"
              onChange={dateAutoFill<IssueInvoiceFormValues>({
                form,
                endDatePath: ["due_date"]
              })}
            />
          </Form.Item>

          <Form.Item<IssueInvoiceFormValues>
            className="flex-1"
            name="due_date"
            label={t("invoices.drawer.fields.dueDate")}
            getValueProps={getDateValueProps}
            dependencies={["date_issued"]}
            rules={createDateRangeRules<IssueInvoiceFormValues>(
              "date_issued",
              "end"
            )}
          >
            <DatePicker className="w-full" />
          </Form.Item>
        </Flex>

        <Flex gap="middle">
          <Form.Item<IssueInvoiceFormValues>
            className="flex-1"
            name="tax_pct"
            label={
              <HintedLabel
                hint={t("invoices.drawer.fields.taxPctHint")}
                testId="invoice-hint-tax"
                field={t("invoices.drawer.fields.taxPct")}
              />
            }
          >
            <InputNumber
              min={0}
              max={100}
              className="w-full"
              suffix="%"
              aria-label={t("invoices.drawer.fields.taxPct")}
            />
          </Form.Item>
          <Form.Item<IssueInvoiceFormValues>
            className="flex-1"
            name="retainage_pct"
            label={
              <HintedLabel
                hint={t("invoices.drawer.fields.retainagePctHint")}
                testId="invoice-hint-retainage"
                field={t("invoices.drawer.fields.retainagePct")}
              />
            }
          >
            <InputNumber
              min={0}
              max={100}
              className="w-full"
              suffix="%"
              aria-label={t("invoices.drawer.fields.retainagePct")}
            />
          </Form.Item>
        </Flex>

        <Title level={5}>{t("invoices.drawer.sections.lines")}</Title>

        {!selectedProject ? (
          <Text type="secondary">{t("invoices.drawer.linesNeedProject")}</Text>
        ) : linesFetching ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : renderLines.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("invoices.drawer.noBillableLines")}
          />
        ) : (
          <Flex vertical gap="small">
            {renderLines.map((line) => {
              const checked = line.id in selectedLines
              const error = lineErrors[line.id]
              const balance = Number(line.balance_remaining)
              const nothingLeftToBill = balance <= 0
              // EAD-1291: balance_remaining hits zero for two different
              // reasons — billed in full, or paid in full without ever being
              // billed (money taken outside the invoicing flow). The old
              // single "Fully billed" tag was wrong for the second case,
              // reading as a contradiction next to "Prev billed: $0".
              const billedInFull =
                Number(line.previously_billed) >= Number(line.planned_value)
              const paidInFullUnbilled =
                nothingLeftToBill &&
                !billedInFull &&
                Number(line.previously_paid ?? "0") >= Number(line.planned_value)
              return (
                <Flex key={line.id} vertical gap={2}>
                  <Flex align="center" justify="space-between" gap="small">
                    <Checkbox
                      checked={checked}
                      disabled={nothingLeftToBill && !checked}
                      onChange={(event) =>
                        toggleLine(line, event.target.checked)
                      }
                    >
                      <Text>{line.name}</Text>
                    </Checkbox>
                    <Flex align="center" gap="small">
                      {billedInFull && (
                        <Tag color="default" className="m-0">
                          {t("invoices.drawer.fullyBilled")}
                        </Tag>
                      )}
                      {paidInFullUnbilled && (
                        <Tag color="default" className="m-0">
                          {t("invoices.drawer.fullyPaidUnbilled")}
                        </Tag>
                      )}
                      <Text type="secondary" className="text-xs">
                        {t("invoices.drawer.balance", {
                          balance: budgetFormat(line.balance_remaining)
                        })}
                      </Text>
                      <FieldHint
                        hint={t("invoices.drawer.balanceHint")}
                        testId={`invoice-hint-balance-${line.id}`}
                        field={t("invoices.drawer.labels.balance")}
                      />
                    </Flex>
                  </Flex>
                  <Flex gap="middle">
                    <HintedLabel
                      hint={t("invoices.drawer.contractHint")}
                      testId={`invoice-hint-contract-${line.id}`}
                      field={t("invoices.drawer.labels.contract")}
                    >
                      <Text type="secondary" className="text-xs">
                        {t("invoices.drawer.contractValue", {
                          value: budgetFormat(line.planned_value)
                        })}
                      </Text>
                    </HintedLabel>
                    <HintedLabel
                      hint={t("invoices.drawer.prevBilledHint")}
                      testId={`invoice-hint-prev-billed-${line.id}`}
                      field={t("invoices.drawer.labels.prevBilled")}
                    >
                      <Text type="secondary" className="text-xs">
                        {t("invoices.drawer.prevBilled", {
                          value: budgetFormat(line.previously_billed)
                        })}
                      </Text>
                    </HintedLabel>
                  </Flex>
                  {checked && (
                    <Flex vertical>
                      <Flex gap="small">
                        <InputNumber
                          value={selectedLines[line.id]}
                          prefix="$"
                          className="w-full"
                          data-testid={`line-amount-${line.id}`}
                          status={error ? "error" : undefined}
                          onChange={(value) =>
                            setLineAmount(line, value as number | null)
                          }
                        />
                        <Button
                          disabled={balance <= 0}
                          onClick={() => setLineAmount(line, balance)}
                        >
                          {t("invoices.drawer.max")}
                        </Button>
                      </Flex>
                      {error && (
                        <Text type="danger" className="text-xs">
                          {error}
                        </Text>
                      )}
                    </Flex>
                  )}
                </Flex>
              )
            })}
          </Flex>
        )}

        <Title level={5} className="mt-4">
          {t("invoices.drawer.sections.totals")}
        </Title>
        <Flex vertical gap={2}>
          <Flex justify="space-between">
            <HintedLabel
              hint={t("invoices.drawer.totals.subtotalHint")}
              testId="invoice-hint-subtotal"
              field={t("invoices.drawer.totals.subtotal")}
            >
              <Text type="secondary">
                {t("invoices.drawer.totals.subtotal")}
              </Text>
            </HintedLabel>
            <Text>{budgetFormat(String(totals.subtotal))}</Text>
          </Flex>
          <Flex justify="space-between">
            <HintedLabel
              hint={t("invoices.drawer.totals.taxHint")}
              testId="invoice-hint-tax-total"
              field={t("invoices.drawer.totals.tax")}
            >
              <Text type="secondary">{t("invoices.drawer.totals.tax")}</Text>
            </HintedLabel>
            <Text>{budgetFormat(String(totals.taxAmount))}</Text>
          </Flex>
          <Flex justify="space-between">
            <HintedLabel
              hint={t("invoices.drawer.totals.retainageHint")}
              testId="invoice-hint-retainage-total"
              field={t("invoices.drawer.totals.retainage")}
            >
              <Text type="secondary">
                {t("invoices.drawer.totals.retainage")}
              </Text>
            </HintedLabel>
            <Text>−{budgetFormat(String(totals.retainageAmount))}</Text>
          </Flex>
          <Flex justify="space-between">
            <HintedLabel
              hint={t("invoices.drawer.totals.totalHint")}
              testId="invoice-hint-total"
              field={t("invoices.drawer.totals.total")}
            >
              <Text strong>{t("invoices.drawer.totals.total")}</Text>
            </HintedLabel>
            <Text strong>{budgetFormat(String(totals.amount))}</Text>
          </Flex>
        </Flex>
      </Form>
    </Drawer>
  )
}
